import { type FC, useEffect, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { Table } from "semantic-ui-react";

import { type ConnectedProps, connect } from "react-redux";
import { ListViewBase } from "../../../../../../../components/listView";
import { type ColumnOption } from "../../../../../../../interfaces/columnOptions";
import { type AppDispatch, type RootState } from "../../../../../../Application/globaltypes/redux";
import * as rtnEvents from "../../../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { PerformanceTabSectionList } from "../../../../../Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";
import { reset } from "../../../state/slices/emailCancelSendSlice";
import { fetchEmailSendSessions } from "../../../state/thunks/emailPerformanceThunk";

import { FeatureFlags } from "featureFlags";
import { getRateString, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { TextTruncate } from "../../../../../../../components";
import { Tooltip } from "../../../../../../../components/common/tooltip";
import { RStatus } from "../../../../../../Application/globaltypes/fetchRequest";
import { selectSendSessions } from "../../../state/slices/emailPerformanceSlice";
import { type EmailSendSession } from "../types";
import "./emailSendsList.scss";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { showStatus } from "../../../utils/emailUtils";
import { PerformanceLinkButton } from "features/Library/Common/Buttons/PerformanceLinkButton/PerformanceLinkButton";
import { type AccountType } from "../../../../../../../interfaces/Account";
import dateTimeUtils from "utils/dateTimeUtils";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type EmailSendsListProps = {
  selectSend: (sendEntity: EmailSendSession) => void;
  emailId: number;
  emailFilter: PerformanceWithAccountFilter & { type: AccountType };
  flowId?: number;
} & PropsFromRedux;

export type EmailSendsListListViewBase = {
  CTR: number;
  Clicks: number;
  OpenRate: number;
  Opens: number;
  SendSessionId: number;
  SendSessionDate: string;
  SendSessionStatusId: number;
  Sends: number;
  id: number;
};

export const EmailSendsListComponent: FC<EmailSendsListProps> = ({
  selectSend,
  sendSessions,
  getEmailSends,
  sendStatusUpdating,
  resetCancelSend,
  emailId,
  emailFilter,
  flowId,
  setHasExportData,
}) => {
  const [slicedEntities, setSliceEntities] = useState<EmailSendSession[]>([]);
  const formatDateTime = useReportingFormatWithTimeZone();

  const reportEnabled = useFeatureFlag(FeatureFlags.EmailReport);

  const loading = useMemo(() => {
    return sendSessions.status === RStatus.Pending;
  }, [sendSessions.status]);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: sendSessions.status === RStatus.Got && !!sendSessions.value?.length,
      tooltipContent: "No data available for export. This email contains no data.",
    });
  }, [setHasExportData, sendSessions]);

  useEffect(() => {
    if (!sendStatusUpdating.canceling && (sendStatusUpdating.canceled || sendStatusUpdating.error)) {
      resetCancelSend();
    }
  }, [sendStatusUpdating, resetCancelSend]);

  // Fetch all sends
  useEffect(() => {
    // These parameters aren't currently available for this item,
    // but we should update the API for it later
    if (emailFilter.type !== undefined) {
      getEmailSends(emailId, { ...emailFilter, flowId });
    }
  }, [emailId, getEmailSends, emailFilter, flowId]);

  useEffect(() => {
    const reduxCompleted = !loading;
    if (reduxCompleted) {
      setSliceEntities([...sendSessions.value].slice(0, 10));
    }
  }, [loading, reportEnabled, sendSessions.value]);

  const columnOptions: ColumnOption<EmailSendSession>[] = [
    {
      name: "Session",
      width: 4,
      isSortable: false,
      render: (entity) => {
        // If entity.TimeZone, then we want to display the entity.SendSession in that time zone. Otherwise, display in the accounts time zone.
        const dateTime = entity.TimeZone
          ? dateTimeUtils.reportingFormatWithTimeZone(entity.SendSession, entity.TimeZone)
          : formatDateTime(entity.SendSession);
        return (
          <PerformanceLinkButton className="sessionTooltip email-sends-title" onClick={() => selectSend(entity)}>
            <Tooltip target={<TextTruncate>{dateTime}</TextTruncate>} content={dateTime} position="left center" />
          </PerformanceLinkButton>
        );
      },
    },
    {
      name: "Origin",
      width: 2,
      isSortable: false,
      render: (entity) => entity.Origin || "-",
    },
    {
      name: "Sends",
      width: 2,
      isSortable: false,
      render: (entity) => (entity.Sends ? entity.Sends.toLocaleString() : "-"),
    },
    {
      name: "Opens",
      width: 2,
      isSortable: false,
      render: (entity) => (entity.Opens ? entity.Opens.toLocaleString() : "-"),
    },
    {
      name: "Open rate",
      width: 2,
      isSortable: false,
      render: (entity) => getRateString(entity.OpenRate),
    },
    {
      name: "Clicks",
      width: 2,
      isSortable: false,
      render: (entity) => (entity.Clicks ? entity.Clicks.toLocaleString() : "-"),
    },
    {
      name: "CTR",
      width: 2,
      isSortable: false,
      render: (entity) => getRateString(entity.ClickRate),
    },
    {
      name: "Status",
      width: 3,
      isSortable: false,
      render: showStatus,
    },
  ];

  const buildTableBody = (listItem: EmailSendSession) => (
    <>
      {columnOptions.map((item, index) => (
        <Table.Cell width={item.width} key={index} disabled={item.disabled?.(listItem)}>
          {item.render(listItem)}
        </Table.Cell>
      ))}
    </>
  );
  const getData = (skip: number, top: number) => {
    setSliceEntities(sendSessions.value.slice(skip, skip + top));
  };

  return (
    <>
      <PerformanceTabSectionList
        title="Sends"
        count={sendSessions.value.length}
        renderList={(page) => {
          const sendEntitiesParsed = slicedEntities.map(({ SendSessionId: id, ...rest }) => ({
            id,
            ...rest,
          }));

          return (
            <ListViewBase
              filter={emailFilter}
              columnOptions={columnOptions}
              loadPage={getData}
              isLoading={loading}
              loadSpinner
              items={sendEntitiesParsed}
              itemsAmount={sendSessions.value.length}
              buildTableBody={(e: EmailSendsListListViewBase) => {
                const { id, ...entityUnparsed } = e;
                entityUnparsed.SendSessionId = e.id;
                return buildTableBody(entityUnparsed as any);
              }}
              withFooter={false}
              activePage={page}
              withSelection={false}
              updateComponentRtnEvents={[rtnEvents.CancelSendSessionSuccess]}
            />
          );
        }}
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  sendStatusUpdating: state.library.emails.emailSendSession.cancel,
  sendSessions: selectSendSessions(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getEmailSends: bindActionCreators(fetchEmailSendSessions, dispatch),
  resetCancelSend: bindActionCreators(reset, dispatch),
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const EmailSendsList = connector(EmailSendsListComponent);
const ConnectedComponent = connector(EmailSendsListComponent);
export default ConnectedComponent;
